import React, { useState, useCallback, useEffect } from "react";
import {
    Box,
    Typography,
    Paper,
    Container,
    Button,
    IconButton,
    Chip,
    Grid,
    Dialog,
    DialogContent,
} from "@mui/material";
import {
    Favorite as LifeIcon,
    Refresh as RefreshIcon,
    CheckCircle,
    Cancel,
    CollectionsBookmark as FlashCardsIcon,
    Close as CloseIcon,
    ArrowBackIosNew as ArrowLeftIcon,
    ArrowForwardIos as ArrowRightIcon,
} from "@mui/icons-material";
import { useParams, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import QuizStyleFlashCards from "./QuizStyleFlashCards";
import { getRandomWordWithHint } from "../../utils/flashCardWords";
import api from "../../api";
import {
    STUDENT_GAME_SUBJECTS,
    MATH_TOPICS,
    ENGLISH_TOPICS,
    VERBAL_REASONING_TOPICS,
    NON_VERBAL_REASONING_TOPICS,
    getStudentGameSubjectByKey,
} from "../../config/studentGamesConfig";
import { FLASHCARD_TOPIC_CONTENT } from "../../config/flashCardTopicContent";
import { renderVisualCard, renderVisualCardBack } from "../../components/FlashCardVisuals";
import DOMPurify from "dompurify";

/** Strip HTML and return visible text length (matches admin quill `plainTextLength` behaviour). */
function plainTextLength(html) {
    return String(html || "")
        .replace(/<[^>]*>/g, "")
        .trim().length;
}

/** Renders flash card description from API (plain text or HTML from admin Quill). */
function FlashCardDescriptionRich({ html, sx }) {
    const clean = DOMPurify.sanitize(String(html ?? ""), { USE_PROFILES: { html: true } });
    if (!clean) return null;
    return (
        <Box
            component="div"
            sx={{
                "& p": { margin: 0 },
                "& p + p": { mt: 0.5 },
                "& ul, & ol": { pl: 2, my: 0.5 },
                ...sx,
            }}
            dangerouslySetInnerHTML={{ __html: clean }}
        />
    );
}

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
const MAX_LIVES = 5;

/** Main flash cards content (vocabulary game + topic cards). All hooks live here so they're never conditional. */
const FlashCardsMain = ({ subjectKey, location, navigate }) => {
    const subjectFromConfig = subjectKey ? getStudentGameSubjectByKey(subjectKey) : null;
    const subject = subjectFromConfig || {
        key: subjectKey || "vocabulary",
        label: "Vocabulary",
    };
    const isSidebarFlashCards = location.pathname.startsWith("/student/flash-cards");

    const [current, setCurrent] = useState(() => getRandomWordWithHint(subjectKey));
    const word = current?.word ?? "";
    const hint = current?.hint ?? "";
    const [guessed, setGuessed] = useState(new Set());
    const [wrongGuesses, setWrongGuesses] = useState(new Set());
    const [hoverLetter, setHoverLetter] = useState(null);
    const [gameOver, setGameOver] = useState(null);
    const [hintsLeft, setHintsLeft] = useState(3);
    const [extraHints, setExtraHints] = useState([]);
    const [selectedTopicKey, setSelectedTopicKey] = useState(null); // null = All topics (flash card subject page)
    const [popupCard, setPopupCard] = useState(null); // { card, topicLabel } for flash card popup
    const [popupFlipped, setPopupFlipped] = useState(false); // true = back side (word description)
    const [popupExampleIndex, setPopupExampleIndex] = useState(0); // index within card.examples
    const [studentSchoolYear, setStudentSchoolYear] = useState(null); // from profile (e.g. 3, 4, 5); content is filtered by this only
    /** Flash cards from API (active only). Keyed by topic_slug -> list of cards. When set, overrides static config. */
    const [topicCardsFromApi, setTopicCardsFromApi] = useState({});
    /** True after a successful API response for current subject — use API data only (even if empty), so inactive cards don't reappear from static. */
    const [flashCardsApiFetched, setFlashCardsApiFetched] = useState(false);

    const letters = word.split("");
    const allRevealed = letters.every((l) => guessed.has(l));
    const livesLeft = MAX_LIVES - wrongGuesses.size;

    const handleLetterClick = useCallback(
        (letter) => {
            if (gameOver) return;
            if (guessed.has(letter) || wrongGuesses.has(letter)) return;
            const inWord = letters.includes(letter);
            if (inWord) {
                setGuessed((prev) => new Set([...prev, letter]));
            } else {
                setWrongGuesses((prev) => {
                    const next = new Set([...prev, letter]);
                    if (next.size >= MAX_LIVES) setGameOver("lose");
                    return next;
                });
            }
        },
        [gameOver, guessed, wrongGuesses, letters]
    );

    useEffect(() => {
        if (!gameOver && allRevealed) setGameOver("win");
    }, [allRevealed, gameOver]);

    useEffect(() => {
        // When subject or word pool changes via URL, start fresh.
        setCurrent(getRandomWordWithHint(subjectKey));
        setGuessed(new Set());
        setWrongGuesses(new Set());
        setHoverLetter(null);
        setGameOver(null);
        setHintsLeft(3);
        setExtraHints([]);
        setSelectedTopicKey(null);
    }, [subjectKey]);

    const fetchFlashCardsFromApi = useCallback(() => {
        if (!subjectKey) {
            setTopicCardsFromApi({});
            setFlashCardsApiFetched(false);
            return;
        }
        api.get("student/flash-cards", { params: { subject_key: subjectKey } })
            .then((res) => {
                if (!res.data?.success || !Array.isArray(res.data?.data?.cards)) {
                    setTopicCardsFromApi({});
                    setFlashCardsApiFetched(false);
                    return;
                }
                const cards = res.data.data.cards;
                const byTopic = {};
                cards.forEach((c) => {
                    const slug = c.topic_slug || "other";
                    if (!byTopic[slug]) byTopic[slug] = [];
                    byTopic[slug].push({
                        ...c,
                        ...(c.content && typeof c.content === "object" ? c.content : {}),
                        icon: c.content?.icon ?? c.title?.[0] ?? "📖",
                        image_url: c.image_url,
                    });
                });
                setTopicCardsFromApi(byTopic);
                setFlashCardsApiFetched(true);
            })
            .catch(() => {
                setTopicCardsFromApi({});
                setFlashCardsApiFetched(false);
            });
    }, [subjectKey]);

    // Fetch flash cards from API (active only). Refetch when tab becomes visible so admin-created/updated cards display.
    useEffect(() => {
        if (!isSidebarFlashCards || !subjectKey) {
            setTopicCardsFromApi({});
            setFlashCardsApiFetched(false);
            return;
        }
        fetchFlashCardsFromApi();
    }, [isSidebarFlashCards, subjectKey, fetchFlashCardsFromApi]);

    useEffect(() => {
        if (!isSidebarFlashCards || !subjectKey) return;
        const onVisibilityChange = () => {
            if (document.visibilityState === "visible") fetchFlashCardsFromApi();
        };
        document.addEventListener("visibilitychange", onVisibilityChange);
        return () => document.removeEventListener("visibilitychange", onVisibilityChange);
    }, [isSidebarFlashCards, subjectKey, fetchFlashCardsFromApi]);

    const resetGame = () => {
        setCurrent(getRandomWordWithHint(subjectKey));
        setGuessed(new Set());
        setWrongGuesses(new Set());
        setHoverLetter(null);
        setGameOver(null);
        setHintsLeft(3);
        setExtraHints([]);
    };

    useEffect(() => {
        if (popupCard) setPopupExampleIndex(0);
    }, [popupCard]);

    useEffect(() => {
        if (!isSidebarFlashCards) return;
        const fetchProfile = async () => {
            try {
                const res = await api.get("student/profile");
                if (res.data?.success && res.data?.data?.school_year) {
                    const match = res.data.data.school_year.match(/\d+/);
                    if (match) setStudentSchoolYear(parseInt(match[0], 10));
                }
            } catch {
                // Student may not have profile yet
            }
        };
        fetchProfile();
    }, [isSidebarFlashCards]);

    useEffect(() => {
        if (!popupCard?.card) return;
        const card = popupCard.card;
        const examples = Array.isArray(card.examples) && card.examples.length ? card.examples : [];
        const total = examples.length;
        if (total <= 1) return;
        const handleKeyDown = (e) => {
            if (e.key === "ArrowLeft") {
                setPopupExampleIndex((prev) => (prev - 1 + total) % total);
                e.preventDefault();
            } else if (e.key === "ArrowRight") {
                setPopupExampleIndex((prev) => (prev + 1) % total);
                e.preventDefault();
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [popupCard]);

    const getLetterState = (letter) => {
        if (guessed.has(letter)) return "correct";
        if (wrongGuesses.has(letter)) return "wrong";
        if (hoverLetter === letter) return "hover";
        return "available";
    };

    const getExtraHintDetail = (targetWord, index) => {
        if (!targetWord) return "";
        const upper = targetWord.toUpperCase();
        const length = upper.length;
        const first = upper[0];
        const last = upper[length - 1];
        const middleIndex = length >= 3 ? Math.floor(length / 2) : 1;
        const middle = upper[middleIndex] ?? first;

        if (index === 1) {
            return `The word begins with the letter "${first}".`;
        }
        if (index === 2) {
            return `One of the middle letters is "${middle}".`;
        }
        if (index === 3) {
            return `The word ends with the letter "${last}".`;
        }
        return "";
    };

    const handleUseHint = () => {
        if (!word || gameOver || hintsLeft <= 0) return;
        const nextIndex = 4 - hintsLeft; // 1..3
        const customHints = Array.isArray(current?.hints) ? current.hints : [];
        const detail = customHints[nextIndex - 1] || getExtraHintDetail(word, nextIndex);
        if (!detail) return;
        setHintsLeft((prev) => prev - 1);
        setExtraHints((prev) => [...prev, detail]);
    };

    const subjectTitleMap = {
        maths: "Maths Vocabulary",
        english: "English Vocabulary",
        "verbal-reasoning": "Verbal Reasoning Vocabulary",
        "non-verbal-reasoning": "Non-Verbal Vocabulary",
    };

    const headerTitle = subjectTitleMap[subject.key] || `${subject.label} Vocabulary`;

    // Content is always filtered by student's registered school year (no year selector).
    const effectiveYear = studentSchoolYear;

    // Sidebar Flash Cards: show 4 subjects only (no inner content yet)
    if (isSidebarFlashCards && !subjectKey) {
        return (
            <Container maxWidth="lg" sx={{ py: 3 }}>
                <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box
                        sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 2,
                            bgcolor: "#2ecc71",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: "0 4px 12px rgba(46, 204, 113, 0.35)",
                        }}
                    >
                        <FlashCardsIcon sx={{ fontSize: 28, color: "#fff" }} />
                    </Box>
                    <Box>
                        <Typography variant="h4" fontWeight="bold" color="black" gutterBottom sx={{ mb: 0 }}>
                            Flash Cards
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#374151" }}>
                            {effectiveYear
                                ? `Showing content for Year ${effectiveYear}. Choose a subject to view flash cards.`
                                : "Your school year is not set. Please ask your parent or administrator to add it in your profile — flash cards will then show content for your year."}
                        </Typography>
                    </Box>
                </Box>

                {!effectiveYear && (
                    <Box sx={{ mb: 3, p: 2, borderRadius: 2, bgcolor: "#fef3c7", border: "1px solid #f59e0b" }}>
                        <Typography variant="body2" sx={{ color: "#92400e", fontWeight: 500 }}>
                            Content is shown by school year. Once your profile has a school year, you can use the subjects below.
                        </Typography>
                    </Box>
                )}

                <Box sx={{ mt: 2 }}>
                    <Box component="div" sx={{ mb: 2 }}>
                        <Typography variant="body1" sx={{ color: "#374151" }}>
                            Subjects
                        </Typography>
                    </Box>
                    <Box component={Paper} elevation={0} sx={{ p: 2, borderRadius: 3, bgcolor: "#f9fafb", border: "1px solid #e5e7eb" }}>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                            {STUDENT_GAME_SUBJECTS.map((subjectItem) => (
                                <Box
                                    key={subjectItem.key}
                                    sx={{
                                        flex: "1 1 220px",
                                        borderRadius: 2,
                                        p: 2,
                                        background: subjectItem.gradient,
                                        border: "1px solid rgba(255,255,255,0.6)",
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 0.75,
                                        cursor: "pointer",
                                        transition: "transform 0.2s ease, box-shadow 0.2s ease",
                                        "&:hover": {
                                            transform: "translateY(-4px)",
                                            boxShadow: "0 8px 20px rgba(15,23,42,0.18)",
                                        },
                                    }}
                                >
                                    <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#111827" }}>
                                        {subjectItem.label}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: "#374151" }}>
                                        {subjectItem.flashCardDescription ?? subjectItem.description}
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={() => navigate(`/student/flash-cards/${subjectItem.key}`)}
                                        sx={{
                                            alignSelf: "flex-start",
                                            mt: 0.5,
                                            borderRadius: 999,
                                            textTransform: "none",
                                            fontWeight: 600,
                                            px: 3,
                                            background: "#111827",
                                            "&:hover": { background: "#030712" },
                                        }}
                                    >
                                        View Flash Cards
                                    </Button>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Box>
            </Container>
        );
    }

    // Flash Cards subject topics page (from sidebar: /student/flash-cards/:subjectKey)
    if (isSidebarFlashCards && subjectKey) {
        const topicsBySubject = {
            maths: MATH_TOPICS,
            english: ENGLISH_TOPICS,
            "verbal-reasoning": VERBAL_REASONING_TOPICS,
            "non-verbal-reasoning": NON_VERBAL_REASONING_TOPICS,
        };
        const allSubjectTopics = topicsBySubject[subjectKey] || [];
        // When school year is missing from profile, show all topics (previous behaviour) so the page is not empty; banner still prompts to set year.
        const subjectTopics = effectiveYear
            ? allSubjectTopics.filter((t) => !t.years || t.years.includes(effectiveYear))
            : allSubjectTopics;
        const topicCardsBySubject = flashCardsApiFetched
            ? topicCardsFromApi
            : (FLASHCARD_TOPIC_CONTENT[subjectKey] || {});
        const needsYearSelection = !effectiveYear && allSubjectTopics.length > 0;

        return (
            <Container maxWidth="lg" sx={{ py: 3 }}>
                <Box sx={{ mb: 3, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1.5 }}>
                    <Box>
                        <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ color: "#111827" }}>
                            🃏 Flash Cards {effectiveYear ? `— Year ${effectiveYear}` : ""}
                        </Typography>
                        <Typography variant="h6" sx={{ color: "#374151", fontWeight: 600 }}>
                            {subject.label}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#374151" }}>
                            {subject.flashCardDescription ?? subject.description}
                        </Typography>
                    </Box>
                    <Button
                        variant="outlined"
                        onClick={() => navigate("/student/flash-cards")}
                        sx={{
                            borderRadius: 999,
                            textTransform: "none",
                            fontWeight: 600,
                            px: 3,
                        }}
                    >
                        Change Subject
                    </Button>
                </Box>

                {needsYearSelection && (
                    <Box sx={{ mb: 3, p: 2, borderRadius: 2, bgcolor: "#fef3c7", border: "1px solid #f59e0b" }}>
                        <Typography variant="body2" sx={{ color: "#92400e", fontWeight: 500 }}>
                            Your school year is not set in your profile. Flash cards are shown by year — please ask your parent or administrator to set your school year so you can see content here.
                        </Typography>
                    </Box>
                )}

                <Box sx={{ mb: 3, display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {subjectTopics.length > 0 ? (
                        <>
                            <Chip
                                label="All topics"
                                color="primary"
                                variant={selectedTopicKey == null ? "filled" : "outlined"}
                                onClick={() => setSelectedTopicKey(null)}
                                sx={{ fontWeight: 600, cursor: "pointer" }}
                            />
                            {subjectTopics.map((topic) => (
                                <Chip
                                    key={topic.key}
                                    label={topic.label}
                                    color={selectedTopicKey === topic.key ? "primary" : "default"}
                                    variant={selectedTopicKey === topic.key ? "filled" : "outlined"}
                                    onClick={() => setSelectedTopicKey(topic.key)}
                                    sx={{ fontWeight: 600, cursor: "pointer" }}
                                />
                            ))}
                        </>
                    ) : (
                        <Typography variant="body2" sx={{ color: "#374151" }}>
                            Topics for this subject will be added soon.
                        </Typography>
                    )}
                </Box>
                {/* Topic flash cards preview - colorful, child-friendly, uniform size */}
                {subjectTopics.length > 0 && (
                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 1.5, sm: 2, md: 3 },
                            borderRadius: 3,
                            border: "1px solid #e5e7eb",
                            bgcolor: "#ffffff",
                        }}
                    >
                        {(() => {
                            const topicsToShow = selectedTopicKey == null ? subjectTopics : subjectTopics.filter((t) => t.key === selectedTopicKey);
                            const allCards = topicsToShow.flatMap((topic) => {
                                const cards = topicCardsBySubject[topic.key] || [];
                                return cards.map((card) => ({ card, topicLabel: topic.label }));
                            });
                            if (!allCards.length) return null;
                            return (
                                <Grid container spacing={2}>
                                        {allCards.map(({ card, topicLabel }, idx) => {
                                            const visualEl = renderVisualCard(card, {
                                                compact: true,
                                                onClick: () => {
                                                    setPopupCard({ card, topicLabel });
                                                    setPopupFlipped(false);
                                                    setPopupExampleIndex(0);
                                                },
                                            });
                                            if (visualEl) {
                                                return (
                                                    <Grid item xs={12} sm={6} md={4} lg={3} key={`${topicLabel}-${card.title}`}>
                                                        {visualEl}
                                                    </Grid>
                                                );
                                            }
                                            // Default card: icon + title + description
                                            const CARD_COLORS = [
                                                { border: "#38bdf8", bg: "#e0f2fe", iconBg: "#0284c7", iconColor: "#fff" },
                                                { border: "#f472b6", bg: "#fce7f3", iconBg: "#db2777", iconColor: "#fff" },
                                                { border: "#fbbf24", bg: "#fef3c7", iconBg: "#d97706", iconColor: "#fff" },
                                                { border: "#34d399", bg: "#d1fae5", iconBg: "#059669", iconColor: "#fff" },
                                                { border: "#a78bfa", bg: "#ede9fe", iconBg: "#7c3aed", iconColor: "#fff" },
                                                { border: "#fb923c", bg: "#ffedd5", iconBg: "#ea580c", iconColor: "#fff" },
                                                { border: "#2dd4bf", bg: "#ccfbf1", iconBg: "#0d9488", iconColor: "#fff" },
                                                { border: "#c084fc", bg: "#f3e8ff", iconBg: "#9333ea", iconColor: "#fff" },
                                            ];
                                            const c = CARD_COLORS[idx % CARD_COLORS.length];
                                            const iconChar = card.icon ?? card.title?.[0] ?? "📖";
                                            return (
                                                <Grid item xs={12} sm={6} md={4} lg={3} key={`${topicLabel}-${card.title}`}>
                                                    <Box
                                                        onClick={() => {
                                                            setPopupCard({ card, topicLabel });
                                                            setPopupFlipped(false);
                                                            setPopupExampleIndex(0);
                                                        }}
                                                        sx={{
                                                            height: 400,
                                                            minHeight: 400,
                                                            p: 2,
                                                            borderRadius: 4,
                                                            border: `5px solid ${c.border}`,
                                                            bgcolor: c.bg,
                                                            boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
                                                            display: "flex",
                                                            flexDirection: "column",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            textAlign: "center",
                                                            position: "relative",
                                                            overflow: "hidden",
                                                            cursor: "pointer",
                                                            transition: "transform 0.2s, box-shadow 0.2s",
                                                            "&:hover": { transform: "scale(1.02)", boxShadow: "0 6px 20px rgba(0,0,0,0.12)" },
                                                            "&::before": {
                                                                content: '""',
                                                                position: "absolute",
                                                                top: 4,
                                                                left: 4,
                                                                right: 4,
                                                                bottom: 4,
                                                                borderRadius: "inherit",
                                                                border: "2px dashed rgba(255,255,255,0.85)",
                                                                pointerEvents: "none",
                                                            },
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                width: 72,
                                                                height: 72,
                                                                borderRadius: "50%",
                                                                bgcolor: c.iconBg,
                                                                color: c.iconColor,
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                fontSize: "2.25rem",
                                                                fontWeight: 700,
                                                                mb: 1.5,
                                                                flexShrink: 0,
                                                                boxShadow: "0 3px 10px rgba(0,0,0,0.15)",
                                                            }}
                                                        >
                                                            {iconChar}
                                                        </Box>
                                                        <Typography
                                                            variant="subtitle1"
                                                            fontWeight={800}
                                                            sx={{
                                                                color: "#1e293b",
                                                                textTransform: "uppercase",
                                                                letterSpacing: 0.8,
                                                                fontSize: "1rem",
                                                                mb: 1,
                                                                lineHeight: 1.2,
                                                            }}
                                                        >
                                                            {card.title}
                                                        </Typography>
                                                        <FlashCardDescriptionRich
                                                            html={card.description}
                                                            sx={{
                                                                color: "#374151",
                                                                fontSize: "0.9rem",
                                                                lineHeight: 1.45,
                                                            }}
                                                        />
                                                    </Box>
                                                </Grid>
                                            );
                                        })}
                                </Grid>
                            );
                        })()}
                    </Paper>
                )}
                {/* Flash card popup */}
                <Dialog
                    open={!!popupCard}
                    onClose={() => { setPopupCard(null); setPopupFlipped(false); setPopupExampleIndex(0); }}
                    TransitionProps={{ onEntered: () => setPopupExampleIndex(0) }}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: 4,
                            width: 420,
                            minHeight: 540,
                            overflow: "visible",
                        },
                    }}
                >
                    <DialogContent sx={{ p: 0, position: "relative", minHeight: 520, overflowX: "hidden" }}>
                        <Box sx={{ position: "absolute", right: 8, top: 8, zIndex: 1, display: "flex", gap: 0.5, alignItems: "center" }}>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => setPopupFlipped((f) => !f)}
                                sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.8rem" }}
                            >
                                {popupFlipped ? "View images" : "View description"}
                            </Button>
                            <IconButton
                                onClick={() => { setPopupCard(null); setPopupFlipped(false); setPopupExampleIndex(0); }}
                                sx={{ bgcolor: "rgba(0,0,0,0.04)", "&:hover": { bgcolor: "rgba(0,0,0,0.08)" } }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </Box>
                        {popupCard && (() => {
                            const { card, topicLabel } = popupCard;
                            const baseCard = card || {};
                            const examples = Array.isArray(baseCard.examples) && baseCard.examples.length
                                ? baseCard.examples
                                : [{}];
                            const totalExamples = examples.length;
                            const hasMultipleExamples = totalExamples > 1;
                            const active = examples[Math.min(popupExampleIndex, totalExamples - 1)] || {};
                            const mergedCard = { ...baseCard, ...active };
                            const contentWrapperSx = { minHeight: 480, display: "flex", flexDirection: "column" };
                            const goExample = (dir) => {
                                if (!hasMultipleExamples) return;
                                setPopupExampleIndex((prev) => {
                                    const next = (prev + dir + totalExamples) % totalExamples;
                                    return next;
                                });
                            };

                            let content;
                            const backHtml =
                                plainTextLength(mergedCard.body) > 0 ? mergedCard.body : mergedCard.description;
                            if (popupFlipped) {
                                // Back side: word description of what the images represent
                                const visualBack = renderVisualCardBack(mergedCard, topicLabel);
                                if (visualBack) {
                                    content = <Box sx={contentWrapperSx}>{visualBack}</Box>;
                                } else {
                                    content = (
                                        <Box sx={{ ...contentWrapperSx, p: 4, pt: 5, textAlign: "center" }}>
                                            <Typography sx={{ fontSize: "0.8rem", color: "#374151", mb: 1 }}>{topicLabel}</Typography>
                                            <Typography fontWeight={800} sx={{ color: "#1e293b", textTransform: "uppercase", fontSize: "1.2rem", mb: 2, letterSpacing: 0.5 }}>{mergedCard.title}</Typography>
                                            <FlashCardDescriptionRich
                                                html={backHtml}
                                                sx={{ color: "#374151", fontSize: "1rem", lineHeight: 1.7, textAlign: "left" }}
                                            />
                                        </Box>
                                    );
                                }
                            } else {
                                const frontVisual = renderVisualCard(mergedCard, { compact: false });
                                if (frontVisual) {
                                    content = (
                                        <Box sx={{ ...contentWrapperSx, p: 4, pt: 5 }}>
                                            <Typography sx={{ fontSize: "0.8rem", color: "#374151", mb: 1 }}>{topicLabel}</Typography>
                                            {frontVisual}
                                        </Box>
                                    );
                                } else {
                                const CARD_COLORS = [
                                    { border: "#38bdf8", bg: "#e0f2fe", iconBg: "#0284c7" },
                                    { border: "#f472b6", bg: "#fce7f3", iconBg: "#db2777" },
                                    { border: "#fbbf24", bg: "#fef3c7", iconBg: "#d97706" },
                                    { border: "#34d399", bg: "#d1fae5", iconBg: "#059669" },
                                    { border: "#a78bfa", bg: "#ede9fe", iconBg: "#7c3aed" },
                                ];
                                const c = CARD_COLORS[0];
                                const iconChar = mergedCard.icon ?? mergedCard.title?.[0] ?? "📖";
                                content = (
                                    <Box sx={{ ...contentWrapperSx, p: 4, pt: 5, textAlign: "center" }}>
                                        <Typography sx={{ fontSize: "0.8rem", color: "#374151", mb: 1 }}>{topicLabel}</Typography>
                                        <Box sx={{ width: 88, height: 88, borderRadius: "50%", bgcolor: c.iconBg, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "2.75rem", fontWeight: 700, mx: "auto", mb: 2 }}>{iconChar}</Box>
                                        <Typography fontWeight={800} sx={{ color: "#1e293b", textTransform: "uppercase", fontSize: "1.3rem", mb: 2, letterSpacing: 0.8 }}>{mergedCard.title}</Typography>
                                        <FlashCardDescriptionRich
                                            html={mergedCard.description}
                                            sx={{ color: "#374151", fontSize: "1rem", lineHeight: 1.6 }}
                                        />
                                    </Box>
                                );
                            }
                        }

                            if (!hasMultipleExamples) return content;

                            return (
                                <Box sx={{ position: "relative", minHeight: 480 }}>
                                    {content}
                                    <IconButton
                                        onClick={() => goExample(-1)}
                                        sx={{
                                            position: "absolute",
                                            top: "50%",
                                            left: 8,
                                            transform: "translateY(-50%)",
                                            bgcolor: "rgba(0,0,0,0.04)",
                                            "&:hover": { bgcolor: "rgba(0,0,0,0.08)" },
                                        }}
                                    >
                                        <ArrowLeftIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        onClick={() => goExample(1)}
                                        sx={{
                                            position: "absolute",
                                            top: "50%",
                                            right: 8,
                                            transform: "translateY(-50%)",
                                            bgcolor: "rgba(0,0,0,0.04)",
                                            "&:hover": { bgcolor: "rgba(0,0,0,0.08)" },
                                        }}
                                    >
                                        <ArrowRightIcon fontSize="small" />
                                    </IconButton>
                                    <Typography
                                        sx={{
                                            position: "absolute",
                                            bottom: 8,
                                            left: "50%",
                                            transform: "translateX(-50%)",
                                            fontSize: "0.75rem",
                                            color: "#374151",
                                        }}
                                    >
                                        {popupExampleIndex + 1} / {totalExamples}
                                    </Typography>
                                </Box>
                            );
                        })()}
                    </DialogContent>
                </Dialog>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 3 }}>
            <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                    sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        bgcolor: "#2ecc71",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 4px 12px rgba(46, 204, 113, 0.35)",
                    }}
                >
                    <FlashCardsIcon sx={{ fontSize: 28, color: "#fff" }} />
                </Box>
                <Box>
                    <Typography variant="h4" fontWeight="bold" color="black" gutterBottom sx={{ mb: 0 }}>
                        {headerTitle}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#374151" }}>
                        Guess the {subject.label.toLowerCase()} word from the clue. You have 5 lives and up to 3 extra hints.
                    </Typography>
                </Box>
            </Box>

            <Paper
                elevation={0}
                sx={{
                    overflow: "hidden",
                    borderRadius: 3,
                    bgcolor: "#ffffff",
                    p: { xs: 1.5, sm: 2, md: 3 },
                    mb: 3,
                    border: "1px solid #dee2e6",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
                    <Box sx={{ display: "flex", gap: 0.75 }}>
                        {Array.from({ length: MAX_LIVES }, (_, i) => (
                            <Box
                                key={i}
                                sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: "50%",
                                    bgcolor: i < livesLeft ? "#2ecc71" : "#e0e0e0",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    border: "2px solid",
                                    borderColor: i < livesLeft ? "#27ae60" : "#bdbdbd",
                                }}
                            >
                                <LifeIcon
                                    sx={{
                                        fontSize: 24,
                                        color: i < livesLeft ? "#fff" : "#374151",
                                    }}
                                />
                            </Box>
                        ))}
                    </Box>
                    <Button
                        startIcon={<RefreshIcon />}
                        onClick={resetGame}
                        variant="contained"
                        size="small"
                        sx={{
                            color: "#fff",
                            bgcolor: "#3498db",
                            "&:hover": { bgcolor: "#2980b9", boxShadow: "0 4px 14px rgba(52, 152, 219, 0.4)" },
                        }}
                    >
                        New word
                    </Button>
                </Box>

                {/* Clue */}
                <Box
                    sx={{
                        textAlign: "center",
                        mb: 2,
                        px: 2,
                        py: 1.5,
                        borderRadius: 2,
                        background: "linear-gradient(135deg, #e3f2fd 0%, #e8eaf6 50%, #f3e5f5 100%)",
                        border: "1px solid #dee2e6",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                    }}
                >
                    <Typography variant="caption" sx={{ color: "#009688", textTransform: "uppercase", letterSpacing: 1, fontWeight: 600 }}>
                        Clue
                    </Typography>
                    <Typography variant="body1" sx={{ color: "text.primary", fontWeight: 500, mt: 0.5 }}>
                        {hint}
                    </Typography>
                    <Box
                        sx={{
                            mt: 1.5,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 0.75,
                        }}
                    >
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={handleUseHint}
                            disabled={hintsLeft <= 0 || !word || !!gameOver}
                            sx={{
                                borderRadius: 999,
                                textTransform: "none",
                                fontWeight: 600,
                                px: 2.5,
                                borderColor: "#f1c40f",
                                color: hintsLeft > 0 ? "#b9770e" : "#9e9e9e",
                                bgcolor: hintsLeft > 0 ? "rgba(241, 196, 15, 0.12)" : "#f5f5f5",
                                "&:hover": {
                                    borderColor: "#f39c12",
                                    bgcolor: "rgba(241, 196, 15, 0.2)",
                                },
                            }}
                        >
                            Hint {hintsLeft > 0 ? `(${hintsLeft} left)` : "(no hints left)"}
                        </Button>
                        {extraHints.length > 0 && (
                            <Box sx={{ mt: 0.25 }}>
                                {extraHints.map((text, idx) => (
                                    <Typography key={idx} variant="body2" sx={{ color: "#5d4037" }}>
                                        {text}
                                    </Typography>
                                ))}
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* Word slots */}
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        justifyContent: "center",
                        gap: 1,
                        minHeight: 56,
                        mb: 4,
                    }}
                >
                    {letters.map((char, i) => {
                        const isRevealed = guessed.has(char);
                        return (
                            <Box
                                key={`${i}-${char}`}
                                sx={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: 2,
                                    border: "2px solid",
                                    borderColor: isRevealed ? "#27ae60" : "#cccccc",
                                    bgcolor: isRevealed ? "#2ecc71" : "#ffffff",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    boxShadow: isRevealed ? "0 4px 12px rgba(46, 204, 113, 0.4)" : "none",
                                }}
                            >
                                {isRevealed ? (
                                    <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color: "#fff" }}>
                                        {char}
                                    </Typography>
                                ) : (
                                    <Box
                                        component="span"
                                        sx={{
                                            width: 22,
                                            height: 22,
                                            borderRadius: "50%",
                                            bgcolor: "#cccccc",
                                        }}
                                    />
                                )}
                            </Box>
                        );
                    })}
                </Box>

                {/* Win / lose messages */}
                {gameOver === "win" && (
                    <Box sx={{ textAlign: "center", mb: 2 }}>
                        <CheckCircle sx={{ fontSize: 56, color: "#2ecc71" }} />
                        <Typography variant="h6" sx={{ color: "#4a4a4a", fontWeight: 600, mt: 1 }}>
                            Well done! You spelled "{word}" correctly.
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={resetGame}
                            sx={{
                                mt: 1.5,
                                bgcolor: "#2ecc71",
                                "&:hover": { bgcolor: "#27ae60" },
                            }}
                        >
                            Next word
                        </Button>
                    </Box>
                )}
                {gameOver === "lose" && (
                    <Box sx={{ textAlign: "center", mb: 2 }}>
                        <Cancel sx={{ fontSize: 56, color: "#374151" }} />
                        <Typography variant="h6" sx={{ color: "#4a4a4a", fontWeight: 600, mt: 1 }}>
                            The word was "{word}". Try again!
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={resetGame}
                            sx={{
                                mt: 1.5,
                                bgcolor: "#3498db",
                                "&:hover": { bgcolor: "#2980b9" },
                            }}
                        >
                            Try again
                        </Button>
                    </Box>
                )}

                {/* Keyboard */}
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        justifyContent: "center",
                        gap: 1,
                        maxWidth: 520,
                        mx: "auto",
                    }}
                >
                    {ALPHABET.map((letter) => {
                        const state = getLetterState(letter);
                        const isDisabled = state === "correct" || state === "wrong" || gameOver;
                        return (
                            <IconButton
                                key={letter}
                                onClick={() => handleLetterClick(letter)}
                                onMouseEnter={() => setHoverLetter(letter)}
                                onMouseLeave={() => setHoverLetter(null)}
                                disabled={isDisabled}
                                sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: 2,
                                    border: "2px solid",
                                    borderColor:
                                        state === "available"
                                            ? "#dee2e6"
                                            : state === "hover"
                                            ? "#667eea"
                                            : "transparent",
                                    bgcolor:
                                        state === "correct" || state === "wrong"
                                            ? "#ffffff"
                                            : state === "hover"
                                            ? "rgba(102, 126, 234, 0.15)"
                                            : "#ffffff",
                                    color:
                                        state === "correct"
                                            ? "#2ecc71"
                                            : state === "wrong"
                                            ? "#ec407a"
                                            : state === "hover"
                                            ? "#667eea"
                                            : "text.primary",
                                    fontWeight: 700,
                                    fontSize: "1rem",
                                    boxShadow:
                                        state === "correct"
                                            ? "0 0 0 2px #2ecc71, 0 4px 12px rgba(46, 204, 113, 0.45)"
                                            : state === "wrong"
                                            ? "0 0 0 2px #ec407a, 0 4px 12px rgba(236, 64, 122, 0.4)"
                                            : "0 2px 6px rgba(0,0,0,0.06)",
                                    "&:hover": !isDisabled
                                        ? {
                                              bgcolor: state === "hover" ? "rgba(102, 126, 234, 0.2)" : "#f5f5f5",
                                              borderColor: "#667eea",
                                          }
                                        : {},
                                    "&.Mui-disabled": { opacity: 1 },
                                }}
                            >
                                {letter}
                            </IconButton>
                        );
                    })}
                </Box>
            </Paper>
        </Container>
    );
};

const FlashCards = () => {
    const { subjectKey } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const isQuizMode = searchParams.get("mode") === "quiz";

    if (isQuizMode) return <QuizStyleFlashCards />;
    return <FlashCardsMain subjectKey={subjectKey} location={location} navigate={navigate} />;
};

export default FlashCards;
